import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { apiClient, readError } from "@/lib/api";
import { fmtEUR, StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  ArrowLeft, Award, BadgeCheck, Briefcase, Wallet, Star, Scale,
  AlertTriangle, TrendingUp, Mail, Phone, MapPin, Building2, ExternalLink,
  CheckCircle2, XCircle, Crown, Sparkles,
} from "lucide-react";

const STATUS_LABELS = {
  resolved: { label: "Résolu (accord)", color: "bg-emerald-50 text-emerald-800 border-emerald-400" },
  expired: { label: "Expiré 30j+", color: "bg-zinc-100 text-zinc-700 border-zinc-400" },
  mediation: { label: "En médiation", color: "bg-amber-50 text-amber-800 border-amber-400" },
  open: { label: "Ouvert", color: "bg-[#FF4D00] text-white border-[#FF4D00]" },
  closed_admin: { label: "Clos admin", color: "bg-zinc-100 text-zinc-700 border-zinc-400" },
};

export default function AdminArtisanDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    apiClient.get(`/admin/artisans/${id}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(readError(e)));
  }, [id]);

  if (!data) return (
    <div className="min-h-screen bg-[#F7F7F9]">
      <Navbar />
      <div className="p-10 mono text-sm">Chargement…</div>
    </div>
  );

  const { profile, missions, stats, quote_stats, disputes, reviews } = data;
  const riskLevel = disputes.total >= 3 ? "high" : disputes.total >= 1 ? "medium" : "low";

  return (
    <div className="min-h-screen bg-[#F7F7F9]" data-testid="admin-artisan-detail-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <Link to="/admin" className="mono text-xs uppercase tracking-widest text-zinc-500 hover:text-[#FF4D00] inline-flex items-center gap-1" data-testid="back-to-admin">
          <ArrowLeft className="w-3.5 h-3.5" /> Retour console admin
        </Link>

        {/* HEADER */}
        <div className="mt-6 border-2 border-[#0A0A0B] bg-white shadow-[8px_8px_0_#FF4D00]">
          <div className="bg-[#0A0A0B] text-white px-6 py-5 flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Fiche artisan</div>
              <div className="display text-3xl mt-2">{profile.name || "—"}</div>
              <div className="text-sm text-zinc-400 mt-1">{profile.email}</div>
              <div className="flex flex-wrap gap-2 mt-3">
                {profile.is_gold && <span className="badge-pill bg-amber-500 text-[#0A0A0B] border-amber-500"><Award className="w-3 h-3 inline mr-1" />Pack Or</span>}
                {profile.is_premium && !profile.is_gold && <span className="badge-pill bg-[#FF4D00] text-white border-[#FF4D00]"><Sparkles className="w-3 h-3 inline mr-1" />Pack Pro</span>}
                {profile.welcome_trial_granted && <span className="badge-pill bg-white text-[#FF4D00] border-[#FF4D00]">🎁 Essai activé</span>}
                {profile.siret_verified && <span className="badge-pill bg-emerald-50 text-emerald-800 border-emerald-400"><BadgeCheck className="w-3 h-3 inline mr-1" />SIRET vérifié</span>}
                {riskLevel === "high" && <span className="badge-pill bg-red-600 text-white border-red-700"><AlertTriangle className="w-3 h-3 inline mr-1" />Risque élevé ({disputes.total} litiges)</span>}
                {riskLevel === "medium" && <span className="badge-pill bg-amber-100 text-amber-800 border-amber-400">{disputes.total} litige{disputes.total > 1 ? "s" : ""} historique</span>}
                {riskLevel === "low" && <span className="badge-pill bg-emerald-50 text-emerald-800 border-emerald-400">✓ Sans litige</span>}
              </div>
            </div>
            <div className="text-right">
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-400">Note moyenne</div>
              <div className="display text-5xl text-[#FF4D00] flex items-center gap-2 justify-end">
                {profile.rating_avg ? profile.rating_avg.toFixed(1) : "—"}
                <Star className="w-8 h-8 fill-[#FF4D00]" />
              </div>
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{profile.rating_count || 0} avis</div>
            </div>
          </div>

          {/* Identity grid */}
          <div className="p-6 grid md:grid-cols-4 gap-3 border-b-2 border-[#0A0A0B]">
            <Mini icon={Building2} label="Entreprise" value={profile.company || "—"} />
            <Mini icon={BadgeCheck} label="SIRET" value={profile.siret || "—"} mono />
            <Mini icon={Phone} label="Téléphone" value={profile.phone || "—"} mono />
            <Mini icon={MapPin} label="Ville" value={profile.city || "—"} />
            {profile.siret_denomination && <Mini label="Dénomination officielle" value={profile.siret_denomination} />}
            {profile.siret_naf && <Mini label="NAF" value={profile.siret_naf} />}
            {profile.premium_until && <Mini label="Fin abonnement Pro" value={profile.premium_until.slice(0, 10)} mono />}
            {profile.gold_until && <Mini label="Fin Pack Or" value={profile.gold_until.slice(0, 10)} mono />}
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b-2 border-[#0A0A0B]">
            <KPI icon={Briefcase} label="Missions" value={stats.missions_total} sub={`${stats.missions_completed} terminées · ${stats.missions_in_progress} en cours`} />
            <KPI icon={Wallet} label="Gains bruts" value={fmtEUR(stats.total_gross)} sub={`Net : ${fmtEUR(stats.total_earnings)}`} accent />
            <KPI icon={TrendingUp} label="Taux devis acceptés" value={`${quote_stats.acceptance_rate}%`} sub={`${quote_stats.accepted}/${quote_stats.total_sent} envoyés`} />
            <KPI icon={Scale} label="Litiges historiques" value={disputes.total} sub={`${disputes.counts.resolved || 0} résolus · ${disputes.counts.expired || 0} expirés`} danger={riskLevel === "high"} />
          </div>

          {/* Payouts breakdown */}
          <div className="p-6 grid md:grid-cols-3 gap-3">
            <div className="border-2 border-[#0A0A0B] p-4">
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500">Gains totaux (net commission)</div>
              <div className="display text-3xl mt-1">{fmtEUR(stats.total_earnings)}</div>
            </div>
            <div className="border-2 border-[#FF4D00] p-4">
              <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">En attente de virement</div>
              <div className="display text-3xl mt-1 text-[#FF4D00]">{fmtEUR(stats.pending_earnings)}</div>
            </div>
            <div className="border-2 border-emerald-600 p-4">
              <div className="mono text-[10px] uppercase tracking-widest text-emerald-700">Déjà versé</div>
              <div className="display text-3xl mt-1 text-emerald-800">{fmtEUR(stats.paid_out_earnings)}</div>
            </div>
          </div>
        </div>

        {/* DISPUTES */}
        {disputes.total > 0 && (
          <div className="mt-8 border-2 border-[#0A0A0B] bg-white">
            <div className="bg-[#0A0A0B] text-white px-5 py-3 flex justify-between items-center">
              <div className="mono text-[11px] uppercase tracking-widest">// Historique litiges ({disputes.total})</div>
              <div className="text-xs mono text-zinc-400">10 plus récents</div>
            </div>
            <div className="p-5 space-y-3">
              {disputes.recent.map((d) => {
                const st = STATUS_LABELS[d.status] || { label: d.status, color: "bg-white border-zinc-300" };
                return (
                  <div key={d.id} className="border-2 border-zinc-200 p-3 flex flex-wrap items-center justify-between gap-3" data-testid={`artisan-dispute-${d.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`badge-pill ${st.color}`}>{st.label}</span>
                        <strong>{d.mission_title || "—"}</strong>
                        <span className="text-xs text-zinc-500">· {fmtEUR(d.amount_escrow)}</span>
                      </div>
                      <div className="text-xs text-zinc-600 mt-1 italic">&quot;{(d.reason || "").slice(0, 180)}{d.reason?.length > 180 ? "…" : ""}&quot;</div>
                    </div>
                    <div className="text-right shrink-0 mono text-[10px] uppercase tracking-widest text-zinc-500">
                      {(d.created_at || "").slice(0, 10)}
                      {d.resolved_at && <> → {d.resolved_at.slice(0, 10)}</>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* MISSIONS */}
        <div className="mt-8 border-2 border-[#0A0A0B] bg-white">
          <div className="bg-[#FF4D00] text-white px-5 py-3">
            <div className="mono text-[11px] uppercase tracking-widest">// Missions ({missions.length})</div>
          </div>
          {missions.length === 0 ? (
            <div className="p-8 text-center mono text-xs text-zinc-500">Aucune mission attribuée.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-100">
                  <tr>
                    <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest">Titre</th>
                    <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest">Demandeur</th>
                    <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest">Catégorie · Ville</th>
                    <th className="text-right px-4 py-3 mono text-[10px] uppercase tracking-widest">Accepté</th>
                    <th className="text-right px-4 py-3 mono text-[10px] uppercase tracking-widest">Payout net</th>
                    <th className="text-center px-4 py-3 mono text-[10px] uppercase tracking-widest">Statut</th>
                    <th className="text-left px-4 py-3 mono text-[10px] uppercase tracking-widest">Date</th>
                    <th className="text-center px-4 py-3 mono text-[10px] uppercase tracking-widest">Ouvrir</th>
                  </tr>
                </thead>
                <tbody>
                  {missions.map((m) => (
                    <tr key={m.id} className="border-t border-zinc-200" data-testid={`artisan-mission-${m.id}`}>
                      <td className="px-4 py-3 font-medium">{m.title}</td>
                      <td className="px-4 py-3 text-xs">{m.demandeur_name || "—"}</td>
                      <td className="px-4 py-3 text-xs">{m.category} · {m.city}</td>
                      <td className="px-4 py-3 text-right">{m.accepted_amount ? fmtEUR(m.accepted_amount) : "—"}</td>
                      <td className="px-4 py-3 text-right font-semibold text-[#FF4D00]">{fmtEUR(m.artisan_payout || 0)}</td>
                      <td className="px-4 py-3 text-center"><StatusBadge status={m.status} /></td>
                      <td className="px-4 py-3 text-xs text-zinc-500">{(m.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3 text-center">
                        <Link to={`/missions/${m.id}`} className="text-[#FF4D00] hover:underline" data-testid={`goto-mission-${m.id}`}>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* REVIEWS */}
        {reviews.length > 0 && (
          <div className="mt-8 border-2 border-[#0A0A0B] bg-white">
            <div className="bg-amber-500 text-[#0A0A0B] px-5 py-3">
              <div className="mono text-[11px] uppercase tracking-widest">// Avis clients ({reviews.length})</div>
            </div>
            <div className="p-5 space-y-3">
              {reviews.slice(0, 8).map((r) => (
                <div key={r.id} className="border-l-4 border-[#FF4D00] pl-3 py-1 bg-zinc-50" data-testid={`review-${r.id}`}>
                  <div className="flex items-center gap-2">
                    <div className="flex text-[#FF4D00]">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className={`w-3 h-3 ${i < (r.rating || 0) ? "fill-[#FF4D00]" : "text-zinc-300"}`} />
                      ))}
                    </div>
                    <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">{(r.created_at || "").slice(0, 10)}</span>
                  </div>
                  <div className="text-sm mt-1">{r.comment || "—"}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Mini({ icon: Icon, label, value, mono }) {
  return (
    <div className="border border-zinc-200 p-3">
      <div className="mono text-[9px] uppercase tracking-widest text-zinc-400 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className={`text-sm mt-1 ${mono ? "mono" : ""}`}>{value}</div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, accent, danger }) {
  const bg = danger ? "bg-red-600 text-white" : accent ? "bg-[#FF4D00] text-white" : "bg-white";
  const iconColor = danger || accent ? "text-white" : "text-[#FF4D00]";
  const subColor = danger || accent ? "text-white/80" : "text-zinc-500";
  return (
    <div className={`p-6 border-r-2 last:border-r-0 border-[#0A0A0B] ${bg}`}>
      <Icon className={`w-6 h-6 ${iconColor}`} />
      <div className={`mono text-[10px] uppercase tracking-widest mt-3 ${subColor}`}>{label}</div>
      <div className="display text-3xl mt-1">{value}</div>
      {sub && <div className={`mono text-[10px] uppercase tracking-widest mt-1 ${subColor}`}>{sub}</div>}
    </div>
  );
}
